import { digObject } from '@domain-group/javascript-utils'
import { useLatestEntity } from '@domain-group/react-hooks'

import useArtworkPages from '@hooks/useArtworkPages'
import { buildEditArtworkUrl } from '@hooks/useArtworkBuilderPaths'
import { generateUrls as generateOrderUrls } from '@hooks/useOrder'
import { generateUrls as generateCampaignQuoteUrls } from '@hooks/useCampaignQuote'
import useCurrentUser from '@hooks/useCurrentUser'
import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { baseURL } from '@functions/api'
import mergeHashQueryParamsWithRedirect from '@functions/mergeHashQueryParamsWithRedirect'

import * as artworkActions from '@redux/modules/artwork'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { EntityOptions } from '@redux/modules/module'

import type {
  ArtworkModel, ArtworkRequestOptions, CampaignModel, MappedArtworkPageDataChange,
} from '@models/types'

import { getCampaignPermissions, getCampaignType } from '@functions/campaign'
import useArtworkReviews from './useArtworkReviews'

export type GenerateUrlsOptions = {
  hashQueryParams?: object,
  includeRedirect?: boolean,
  redirectUrl?: string,
}

export const generateUrls = (
  artwork: Partial<ArtworkModel> = {},
  options: GenerateUrlsOptions = {},
) => {
  const { hashQueryParams, includeRedirect, redirectUrl } = options
  const { campaign_quote_item_id, id, order_item_id } = artwork

  return {
    artworkDownloadPdfUrl: `${baseURL}/artworks/${id}/pdf.json`,
    createArtworkUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/artworks/new',
      hashQueryParams: {
        campaignQuoteItemId: campaign_quote_item_id,
        orderItemId: order_item_id,
        ...hashQueryParams,
      },
      includeRedirect,
      redirectUrl,
    }),
    editArtworkUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/artworks/edit',
      hashQueryParams: {
        artworkId: id,
        ...hashQueryParams,
      },
      includeRedirect,
      redirectUrl,
    }),
  }
}

type CreateArtworkParams = {
  artworkParams: Partial<ArtworkModel>,
  dispatch: AppDispatch,
  requestOptions?: ArtworkRequestOptions,
}

const createArtwork = (params: CreateArtworkParams) => {
  const { artworkParams, dispatch, requestOptions } = params
  const { createArtwork: createFn } = artworkActions

  return dispatch(createFn(artworkParams, requestOptions))
}

type CreateArtworkFromBackupParams = {
  dispatch: AppDispatch,
  generateUrlsOptions?: GenerateUrlsOptions,
  orderItemId: number,
  requestOptions?: ArtworkRequestOptions,
}

const createArtworkFromBackup = async (params: CreateArtworkFromBackupParams) => {
  const {
    dispatch, generateUrlsOptions, orderItemId, requestOptions,
  } = params
  const { createArtworkFromBackup: createFn } = artworkActions

  return dispatch(createFn(orderItemId, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response

      const { editArtworkUrl } = generateUrls(data, generateUrlsOptions)

      return {
        success,
        data,
        redirectUrl: editArtworkUrl,
      }
    }

    return response
  })
}

type CreateArtworkWithCampaignPayload = {
  agency_id: number,
  artwork_page: {
    template_page_id: number,
  },
  campaign?: Partial<CampaignModel>,
  order: {
    order_type: 'item',
    item: {
      price_list_item: {
        id: number,
      },
      options: EntityOptions,
    },
  },
  template_page_category_id?: number,
}

export type CreateArtworkWithCampaignParams = {
  dispatch: AppDispatch,
  payload: CreateArtworkWithCampaignPayload,
  requestOptions: EntityOptions,
}

const createArtworkWithCampaign = (params: CreateArtworkWithCampaignParams) => {
  const { dispatch, payload, requestOptions } = params
  const { createArtworkWithCampaign: createFn } = artworkActions

  const mergedRequestOptions = {
    ...requestOptions,
    include_artwork_agency: true,
    include_artwork_campaign_quote: true,
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(payload, mergedRequestOptions)).then((response) => {
    // @ts-expect-error TS2339
    const { success, data } = response

    if (!success) return response

    const {
      // @ts-expect-error TS2339
      campaign_id, campaign_quote_id, campaign_quote_item_id,
    } = data

    const { pendingCampaignOrdersUrl } = generateOrderUrls({ campaign_id })

    const editArtworkUrl = buildEditArtworkUrl({
      artwork: data,
      redirectUrl: encodeURIComponent(pendingCampaignOrdersUrl),
    })

    const { editCampaignQuoteUrl } = generateCampaignQuoteUrls(
      { id: campaign_quote_id },
      { prefixRoute: `campaigns/${campaign_id}/edit` },
    )

    return {
      ...response,
      redirectUrl: campaign_quote_item_id ? editCampaignQuoteUrl : editArtworkUrl,
    }
  })
}

type LoadArtworkDataChangesParams = {
  artwork: ArtworkModel,
  dispatch: AppDispatch,
  requestOptions?: ArtworkRequestOptions,
}

const loadArtwork = (params: LoadArtworkDataChangesParams) => {
  const { artwork, dispatch, requestOptions } = params
  const { loadArtwork: loadFn } = artworkActions

  return dispatch(loadFn(artwork.id, requestOptions))
}

type PatchArtworkDataParams = {
  artwork: ArtworkModel,
  changes: MappedArtworkPageDataChange[],
  dispatch: AppDispatch,
  requestOptions?: ArtworkRequestOptions,
}

const patchArtworkData = (params: PatchArtworkDataParams) => {
  const {
    artwork, changes, dispatch, requestOptions,
  } = params
  const { patchArtworkData: patchFn } = artworkActions

  return dispatch(patchFn(artwork.id, changes, requestOptions))
}

type UpdateArtworkParams = {
  artwork: ArtworkModel,
  artworkParams: Partial<ArtworkModel>,
  dispatch: AppDispatch,
  requestOptions?: ArtworkRequestOptions,
}

const updateArtwork = (params: UpdateArtworkParams) => {
  const {
    dispatch, artwork, artworkParams, requestOptions,
  } = params
  const { updateArtwork: updateFn } = artworkActions

  const updatedParams = {
    id: artwork.id,
    ...artworkParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type ApproveArtworkParams = {
  artwork: ArtworkModel,
  dispatch: AppDispatch,
  requestOptions?: ArtworkRequestOptions,
}

const approveArtwork = (params: ApproveArtworkParams) => {
  const {
    dispatch, artwork, requestOptions,
  } = params
  const { approveArtwork: approveFn } = artworkActions

  return dispatch(approveFn(artwork.id, requestOptions))
}

type UnapproveArtworkParams = {
  artwork: ArtworkModel,
  dispatch: AppDispatch,
  requestOptions?: ArtworkRequestOptions,
}

const unapproveArtwork = (params: UnapproveArtworkParams) => {
  const {
    dispatch, artwork, requestOptions,
  } = params
  const { unapproveArtwork: unapproveFn } = artworkActions

  return dispatch(unapproveFn(artwork.id, requestOptions))
}

type ArtworkParams = {
  artwork_file: object,
  campaign_id: number,
  order_item_id: number,
}

type UploadArtworkParams = {
  artwork: ArtworkModel,
  artworkParams: ArtworkParams,
  dispatch: AppDispatch,
}

const uploadArtwork = (params: UploadArtworkParams) => {
  const {
    artwork, artworkParams, dispatch,
  } = params
  const { uploadArtwork: uploadArtworkFn } = artworkActions

  const updatedParams = {
    id: artwork.id,
    ...artworkParams,
  }
  return dispatch(uploadArtworkFn(updatedParams))
}

type DeleteArtworkParams = {
  artwork: DeleteParams<ArtworkModel>,
  dispatch: AppDispatch,
}

const deleteArtwork = (params: DeleteArtworkParams) => {
  const { dispatch, artwork } = params
  const { deleteArtwork: deleteFn } = artworkActions

  return dispatch(deleteFn(artwork))
}

export const useRelations = (artwork: Partial<ArtworkModel> = {}) => {
  const {
    campaign_id,
    // campaign_item_id, // TODO - Waiting for model
    campaign_quote_item_id,
    order_item_id,
    service_product_id,
    status_id,
    template_page_category_id,
  } = artwork

  const {
    agencies,
    campaignQuoteItems,
    campaigns,
    orderItems,
    serviceProducts,
    statuses,
    templatePageCategories,
  } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  // @ts-expect-error TS2339
  const agency = campaign_id && agencies[campaign.agency_id] ? agencies[campaign.agency_id] : {}

  const campaignQuoteItem = campaign_quote_item_id && campaignQuoteItems[campaign_quote_item_id]
    ? campaignQuoteItems[campaign_quote_item_id] : {}

  const orderItem = order_item_id && orderItems[order_item_id]
    ? orderItems[order_item_id] : {}

  const serviceProduct = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id] : {}

  const status = status_id && statuses[status_id]
    ? statuses[status_id] : {}

  const templatePageCategory = template_page_category_id && templatePageCategories[template_page_category_id]
    ? templatePageCategories[template_page_category_id] : {}

  return {
    agency,
    campaign,
    campaignQuoteItem,
    orderItem,
    serviceProduct,
    status,
    templatePageCategory,
  }
}

type Options = {
  generateUrlsOptions?: GenerateUrlsOptions,
  performHttpRequests?: boolean,
  requestOptions?: ArtworkRequestOptions,
  selectedPageIndex?: number,
}

function useArtwork(initEntity: Partial<ArtworkModel> = {}, options: Options = {}) {
  const {
    generateUrlsOptions,
    performHttpRequests = false,
    requestOptions,
    selectedPageIndex,
  } = options

  // @ts-expect-error TS2322
  const { entity: artwork }: { entity: ArtworkModel} = useLatestEntity(initEntity, 'artworks')
  const { id } = artwork

  const { currentUser } = useCurrentUser({ performHttpRequests: false })
  const dispatch = useDispatch()

  const {
    campaign,
    orderItem,
    status,
  } = useRelations(artwork)

  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })

  // Load Artwork
  useReduxAction(
    'artworks',
    'loadArtwork',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const allowPrintDownload = campaignPermissions?.artwork?.print_quality || false
  // @ts-expect-error TS2339
  const isApproved = status.key === 'approved'
  const isTemplateArtwork = artwork.artwork_type === 'template'
  const isUploadedArtwork = artwork.artwork_type === 'file'

  // Artwork Pages
  const artworkPagesPayload = useArtworkPages({
    filters: {
      // @ts-expect-error TS2322
      artwork_id: artwork.id || 'undefined', // dont return any if the id is null
    },
    performHttpRequests: false,
  })
  const { filteredArtworkPages } = artworkPagesPayload

  const selectedArtworkPage = (isTemplateArtwork || (artwork.pages?.length))
    ? filteredArtworkPages[selectedPageIndex || 0] : {}

  // Artwork Reviews
  const artworkReviewsPayload = useArtworkReviews({
    filters: {
      artwork_id: artwork.id,
    },
    performHttpRequests: false,
  })
  const { filteredArtworkReviews, hasFilteredArtworkReviews } = artworkReviewsPayload

  const approvedArtworkReviewsPayload = useArtworkReviews({
    filters: {
      artwork_id: artwork.id,
      status_key: 'approved',
    },
    performHttpRequests: false,
  })
  const { filteredArtworkReviews: approvedArtworkReviews } = approvedArtworkReviewsPayload

  const allArtworkReviewsApproved = !!(hasFilteredArtworkReviews
    && filteredArtworkReviews.length === approvedArtworkReviews.length)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.artworks)

  const { agency } = useRelations(artwork)

  // // Paths
  // const artworkBuilderPaths = useArtworkBuilderPaths(artwork, orderItem)

  return {
    agency,
    allArtworkReviewsApproved,
    allowPrintDownload,
    artwork,
    callbacks: {
      approveArtwork: (entityOptions?: ArtworkRequestOptions) => (
        approveArtwork({ artwork, dispatch, requestOptions: entityOptions })
      ),
      createArtwork: (artworkParams: Partial<ArtworkModel>, entityOptions?: ArtworkRequestOptions) => (
        createArtwork({ artworkParams, dispatch, requestOptions: entityOptions })
      ),
      createArtworkFromBackup: (params: { orderItemId: number }, entityOptions?: ArtworkRequestOptions) => (
        createArtworkFromBackup({
          dispatch, generateUrlsOptions, orderItemId: params.orderItemId, requestOptions: entityOptions,
        })
      ),
      createArtworkWithCampaign: (payload: CreateArtworkWithCampaignPayload, entityOptions: EntityOptions) => (
        createArtworkWithCampaign({ dispatch, payload, requestOptions: entityOptions })
      ),
      deleteArtwork: () => deleteArtwork({ artwork, dispatch }),
      loadArtwork: (entityOptions?: ArtworkRequestOptions) => (
        loadArtwork({ artwork, dispatch, requestOptions: entityOptions })
      ),
      patchArtworkData: (changes: MappedArtworkPageDataChange[], entityOptions?: ArtworkRequestOptions) => (
        patchArtworkData({
          artwork, changes, dispatch, requestOptions: entityOptions,
        })
      ),
      unapproveArtwork: (entityOptions?: ArtworkRequestOptions) => (
        unapproveArtwork({ artwork, dispatch, requestOptions: entityOptions })
      ),
      updateArtwork: (artworkParams: Partial<ArtworkModel>, entityOptions?: ArtworkRequestOptions) => (
        updateArtwork({
          artwork, artworkParams, dispatch, requestOptions: entityOptions,
        })
      ),
      uploadArtwork: (artworkParams: ArtworkParams) => (
        uploadArtwork({
          artwork, artworkParams, dispatch,
        })
      ),
    },
    campaign,
    creating,
    deleting,
    filteredArtworkPages,
    filteredArtworkReviews,
    hasFilteredArtworkReviews,
    isApproved,
    isTemplateArtwork,
    isUploadedArtwork,
    loading: loading && !loadedIds.includes(artwork.id),
    orderItem,
    selectedArtworkPage,
    status,
    updating,
    urls: generateUrls(artwork, generateUrlsOptions),
  }
}

export type UseArtworkPayload = ReturnType<typeof useArtwork>

export default useArtwork
